import { Waves, Castle, Trees, Handshake, Scale, Eye, Swords, BookOpen } from "lucide-react";
import { ASSETS } from "@/assets";
import { Reveal } from "@/components/Reveal";
import { DISCORD_URL, WIKI_URL } from "@/constants/links";
import { LORE_PAGE } from "@/constants/testIds";

const NEIGHBORS = [
  {
    icon: Handshake,
    status: "Spojenec",
    color: "emerald",
    name: "Hůrka",
    desc: "Malá farmářská osada založená osadníky z nedalekého Údolí (Dalelands). Jediný opravdový spojenec Rowengardu.",
  },
  {
    icon: Scale,
    status: "Křehký mír",
    color: "amber",
    name: "Rabenburg",
    desc: "Vojenská a důlní pevnost, kterou zem přišla obsadit pro své pány z Mulmasteru, jednoho z despotických států Lunomoří.",
  },
  {
    icon: Eye,
    status: "Vlažné vztahy",
    color: "slate",
    name: "Klášter Svatého Aurelia",
    desc: "Náboženská skupina uctívačů Ilmatera, která si zakládá na tajnůstkaření.",
  },
  {
    icon: Swords,
    status: "Válka",
    color: "red",
    name: "„Král Banditů“",
    desc: "V nedávných letech se ve Vranodolí objevil s početnou posádkou banditů, vyvrhelů a dezertérů, odhodlaných zem vyplenit.",
  },
];

const STATUS_CLASSES = {
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  slate: "border-white/20 bg-white/[0.06] text-white/60",
  red: "border-red-500/30 bg-red-500/10 text-red-400",
};

export const LorePage = () => {
  return (
    <section data-testid={LORE_PAGE.section}>
      {/* Hero */}
      <div className="relative overflow-hidden px-6 pb-16 pt-40" data-testid={LORE_PAGE.heroSection}>
        <img
          src={ASSETS.heroCastle}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-[65%_center] brightness-[0.85]"
        />
        <div className="absolute inset-0 bg-[#141417]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#141417]/70 to-[#141417]" />
        <div className="relative mx-auto max-w-[900px] text-center">
          <Reveal>
            <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-[#F0801F]">Lore</p>
            <h1 className="mt-4 font-display text-5xl font-600 uppercase tracking-tight text-white sm:text-6xl">
              Rowengard a Vranodolí
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/60">
              Nové město na krutém pobřeží Lunomoří, postavené na zemi, která pohřbila
              každého, kdo si ji předtím zkusil podmanit.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="px-6">
        <Reveal
          data-testid={LORE_PAGE.disclaimer}
          className="mx-auto flex max-w-[1000px] items-start gap-5 rounded-xl border border-[#F0801F]/30 bg-[#17171b] p-8"
        >
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full border border-[#F0801F]/30 text-[#F0801F]">
            <BookOpen className="h-7 w-7" strokeWidth={1.6} />
          </span>
          <p className="text-lg leading-relaxed text-white/70">
            <span className="font-display text-xl font-600 uppercase tracking-wide text-[#F0801F]">
              Strohý výklad pro nové hráče.
            </span>{" "}
            Tahle stránka ti dá jen základy příběhu, aby ses v lore rychle zorientoval(a).
            Podrobné lore, historii, postavy i frakce najdeš na naší wiki - odkaz čeká na konci
            stránky.
          </p>
        </Reveal>
      </div>

      {/* Lunomoří */}
      <div className="px-6 py-16 lg:py-20" data-testid={LORE_PAGE.seaSection}>
        <div className="mx-auto grid max-w-[1584px] grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Moře na prahu domova
            </p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
              Lunomoří
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/60">
              Děj Draconia RP se odehrává v novém městě Rowengard na jižním pobřeží Lunomoří
              (Moonsea). Lunomoří je nezkrocené, nebezpečné moře plné pirátů a mořských bestií,
              obklopené despotickými státy, pro které má lidský život nízkou hodnotu.
            </p>
            <p className="mt-4 text-base leading-relaxed text-white/60">
              Ale je to také moře mnoha možností, bohatství a nerostných surovin, o které vedou
              jeho obyvatelé pravidelné šarvátky.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex justify-center">
            <span className="grid h-40 w-40 place-items-center rounded-full border border-[#F0801F]/20 bg-[#17171b] text-[#F0801F] sm:h-52 sm:w-52">
              <Waves className="h-16 w-16 sm:h-20 sm:w-20" strokeWidth={1.2} />
            </span>
          </Reveal>
        </div>
      </div>

      {/* Rowengard */}
      <div className="px-6 py-16 lg:py-20" data-testid={LORE_PAGE.citySection}>
        <div className="mx-auto max-w-[1584px] overflow-hidden rounded-xl border border-white/10 bg-[#17171b]">
          <Reveal className="overflow-hidden">
            <img
              src={ASSETS.rowengardNight}
              alt="Noční pohled na hradby a střechy Rowengardu pod srpkem měsíce"
              loading="lazy"
              className="h-64 w-full object-cover sm:h-80"
            />
          </Reveal>
          <div className="px-6 py-14 sm:px-10 lg:py-16">
            <Reveal className="mb-10 flex flex-col items-center text-center">
              <span className="mb-5 grid h-16 w-16 place-items-center rounded-full border border-[#F0801F]/30 text-[#F0801F]">
                <Castle className="h-7 w-7" strokeWidth={1.4} />
              </span>
              <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-[#F0801F]">
                Mladé město
              </p>
              <h2 className="mt-3 font-display text-4xl font-600 uppercase tracking-tight text-white sm:text-5xl">
                Rowengard
              </h2>
            </Reveal>
            <div className="mx-auto max-w-3xl space-y-4 text-base leading-relaxed text-white/60">
              <p>
                Rowengard je velice mladé město a vzniklo teprve před patnácti lety. Během
                tohoto období však bylo (hlavně díky jeho bohatým dolům) postaveno pevné, solidní
                opevnění, farmy, infrastruktura a brzy se stalo cílem mnoha dobrodruhů a osadníků
                z celého, širého okolí. Nové pevnostní město se tak stalo velmi kosmopolitním
                místem s populací stálých obyvatel kolem tří tisíc občanů.
              </p>
              <p>
                Patronem Rowengardu je království Cormyr, jedno z nejsilnějších království na
                jihu, které je známé spravedlností a ctí. Cormyr nejspíše doufá, že jejich
                přítomnost pomůže do Lunomoří přinést řád a ukázat zdejšímu lidu, že jejich
                životy mají větší hodnotu, než jak je zde naceněna. Městu vládne{" "}
                <span className="text-white/85">Baron Caswallon</span>, muž který zem dobyl se
                svojí posádkou a město založil.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vranodolí */}
      <div className="px-6 py-16 lg:py-20" data-testid={LORE_PAGE.regionSection}>
        <div className="mx-auto grid max-w-[1584px] grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal delay={0.1} className="order-2 flex justify-center lg:order-1">
            <span className="grid h-40 w-40 place-items-center rounded-full border border-[#F0801F]/20 bg-[#17171b] text-[#F0801F] sm:h-52 sm:w-52">
              <Trees className="h-16 w-16 sm:h-20 sm:w-20" strokeWidth={1.2} />
            </span>
          </Reveal>
          <Reveal className="order-1 lg:order-2">
            <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Prokletá a divoká zem
            </p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
              Vranodolí
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/60">
              Země, v které se Rowengard nachází, se nazývá Vranodolí - země, která je
              považována za prokletou a velice divokou. Stojí na pomezí mýtického lesa
              Cormanthoru, který je domovem divokých i temných elfů, nebezpečných monster a
              nedávno navrátivšího se Myth Drannoru, mocného města slunečních elfů.
            </p>
            <p className="mt-4 text-base leading-relaxed text-white/60">
              Vranodolí samotné však není opuštěné a nachází se zde orkové, ještěřani, nemrtví a
              duchové minulosti. Zem samotná zažila mnohé říše a království, každá z nich se
              podepsala a zdobí krajinu hojnými ruinami z divoce odlišných dob. Každý, kdo se
              Vranodolí snažil zkrotit, nyní hnije v jeho zemině. A Rowengard tentokrát není
              samotný, kdo si zem přišel uzmout pro sebe…
            </p>
          </Reveal>
        </div>
      </div>

      {/* Sousedé a spojenci */}
      <div className="px-6 py-16 lg:py-20" data-testid={LORE_PAGE.neighborsSection}>
        <div className="mx-auto max-w-[1584px]">
          <Reveal className="mb-12 text-center">
            <p className="font-display text-sm font-600 uppercase tracking-[0.3em] text-[#F0801F]">
              Kdo sdílí Vranodolí
            </p>
            <h2 className="mt-3 font-display text-4xl font-600 uppercase tracking-tight text-white sm:text-5xl">
              Sousedé a spojenci
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/60">
              Za těchto patnáct let se ve Vranodolí usídlilo mnoho lidských osadníků, kteří si
              každý snaží zem přivlastnit a vybudovat nový domov.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {NEIGHBORS.map((n, i) => {
              const Icon = n.icon;
              return (
                <Reveal
                  key={n.name}
                  delay={i * 0.06}
                  data-testid={LORE_PAGE.neighborCard}
                  className="rounded-xl border border-white/10 bg-[#17171b] p-6"
                >
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-display text-xs font-600 uppercase tracking-[0.1em] ${STATUS_CLASSES[n.color]}`}
                  >
                    <Icon className="h-3 w-3" strokeWidth={2} />
                    {n.status}
                  </span>
                  <h3 className="mt-4 font-display text-lg font-600 uppercase tracking-tight text-white">
                    {n.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/50">{n.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>

      {/* Divoká krajina Vranodolí */}
      <div className="px-6 pb-16 lg:pb-20">
        <Reveal className="mx-auto max-w-[1584px] overflow-hidden rounded-xl border border-white/10">
          <img
            src={ASSETS.vranodoliWilds}
            alt="Divoká krajina Vranodolí - skalnaté údolí zarostlé lesem se vzdálenou věží"
            loading="lazy"
            className="h-64 w-full object-cover sm:h-96"
          />
        </Reveal>
      </div>

      {/* Uzávěr */}
      <div className="px-6 pb-16 lg:pb-20">
        <Reveal className="mx-auto max-w-[900px] text-center">
          <p className="text-base leading-relaxed text-white/60">
            Rowengard má před sebou stále dlouhou cestu, než bude Vranodolí bezpečné a svobodné,
            ale vyhlašuje se za zemi nových začátků. Každý, kdo sem přicestuje, může přiložit
            ruku k dílu a vydobýt si místo pro život, nehledě na to, jaká je jeho minulost. A
            její četné ruiny lákají badatele, dobrodruhy a oportunisty, kteří doufají ve skryté
            poklady.
          </p>
        </Reveal>
      </div>

      {/* CTA na wiki */}
      <div className="px-6 pb-24" data-testid={LORE_PAGE.ctaSection}>
        <Reveal className="mx-auto max-w-[1584px] rounded-xl border border-[#F0801F]/30 bg-[#17171b] px-6 py-14 text-center sm:px-10">
          <h2 className="font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
            Tohle byl jen začátek
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/60">
            Podrobnější lore, historii Faerûnu, frakce, postavy i místa najdeš na naší wiki.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={WIKI_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-[#F0801F] px-8 py-4 font-display text-sm font-600 uppercase tracking-[0.14em] text-[#141417] transition-colors hover:bg-[#ff9438]"
            >
              <BookOpen className="h-4 w-4" />
              Otevřít wiki
            </a>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-8 py-4 font-display text-sm font-600 uppercase tracking-[0.14em] text-white transition-colors hover:border-[#F0801F] hover:text-[#F0801F]"
            >
              <i className="fa-brands fa-discord" />
              Discord
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
